"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { WALLET_SIGNER_STATUS, useWalletSignerStatusStore } from "@/store/walletSignerStatusStore";
import { usePrivy } from "@privy-io/react-auth";
import { useWallets } from "@privy-io/react-auth/solana";

/**
 * Error thrown when trying to link a Solana wallet that's already linked to another account.
 * This helps users understand they need to unlink from their other account first.
 */
export class SolanaWalletAlreadyLinkedError extends Error {
  constructor() {
    super(
      "This Solana wallet is already linked to another account. " +
        "To use it here, you need to unlink it from your other account first. " +
        "Go to Profile settings on that account to unlink.",
    );
    this.name = "SolanaWalletAlreadyLinkedError";
  }
}

/**
 * Helper to check if a linked account is an embedded wallet (created by Privy).
 * Embedded wallets have connectorType "embedded" - they cannot be unlinked,
 * only external wallets (Phantom, Solflare, etc.) can be unlinked.
 */
const isEmbeddedWallet = (account: { connectorType?: string }): boolean => {
  return account.connectorType?.toLowerCase() === "embedded";
};

/**
 * Hook for managing Solana wallet connection for transaction signing.
 *
 * IMPORTANT: This hook is ONLY for transaction signing on Solana DEXs.
 * It does NOT provide authentication - auth is always via EVM wallet.
 *
 * The Solana wallet connected here is separate from the EVM wallet used for:
 * - Backend authentication (identity token)
 * - Session management
 * - User identification
 *
 * LINKING: We use linkWallet() which both connects AND links in one step.
 * This is required for backend verification that the wallet belongs to the user.
 *
 * UNLINKING: Users can unlink their Solana wallet from Profile settings.
 * This allows them to link it to a different EVM account if needed.
 *
 * NOTE: Embedded wallets (created by Privy) are excluded - they cannot be unlinked
 * and are not usable for transaction signing on external DEXs.
 */
export const useSolanaWallet = () => {
  // Get connected Solana wallets (Phantom, Solflare, etc.)
  // In Privy v3, useWallets returns both embedded and EOA wallets
  const { wallets: standardWallets } = useWallets();

  // Get user's linked accounts and linkWallet/unlinkWallet functions
  const { user, linkWallet, unlinkWallet } = usePrivy();

  // Track linking errors (e.g., wallet already linked to another account)
  const [linkError, setLinkError] = useState<Error | null>(null);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const solanaSignerStatus = useWalletSignerStatusStore((state) => state.solanaSignerStatus);
  const setSolanaSignerStatus = useWalletSignerStatusStore((state) => state.setSolanaSignerStatus);

  // Find the linked EXTERNAL (non-embedded) Solana wallet address first
  // We need this to match against connected wallets
  const linkedExternalSolanaAddress = useMemo(() => {
    if (!user?.linkedAccounts) return null;

    const solanaAccount = user.linkedAccounts.find((account) => {
      if (account.type !== "wallet" || !("chainType" in account) || account.chainType !== "solana") {
        return false;
      }
      // Exclude embedded wallets - they have connector_type "embedded"
      return !isEmbeddedWallet(account as { connectorType?: string });
    });

    return solanaAccount && "address" in solanaAccount ? (solanaAccount as { address: string }).address : null;
  }, [user?.linkedAccounts]);

  // Find the currently active INJECTED wallet from standardWallets
  // Filter out embedded wallets by checking if it matches the linked embedded address
  const activeInjectedWallet = useMemo(() => {
    if (!standardWallets.length) return null;

    // Get embedded wallet address from linked accounts to filter it out
    const embeddedSolanaAddress = user?.linkedAccounts?.find((account) => {
      if (account.type !== "wallet" || !("chainType" in account) || account.chainType !== "solana") {
        return false;
      }
      return isEmbeddedWallet(account as { connectorType?: string });
    });
    const embeddedAddress =
      embeddedSolanaAddress && "address" in embeddedSolanaAddress
        ? (embeddedSolanaAddress as { address: string }).address
        : null;

    // Find the first non-embedded wallet (injected wallet like Phantom)
    return (
      standardWallets.find(
        (wallet) => !embeddedAddress || wallet.address.toLowerCase() !== embeddedAddress.toLowerCase(),
      ) ?? null
    );
  }, [standardWallets, user?.linkedAccounts]);

  // Select the connected standard wallet that matches our linked EXTERNAL wallet
  // IMPORTANT: useConnectedStandardWallets can return embedded wallets too!
  // We must match against linkedExternalSolanaAddress to get the injected wallet (Phantom)
  const solanaWallet = useMemo(() => {
    if (!standardWallets.length) return null;

    // MUST match against linkedExternalSolanaAddress to get the injected wallet
    // The standard wallets array can contain both embedded and injected wallets
    if (linkedExternalSolanaAddress) {
      const matchingWallet = standardWallets.find(
        (wallet) => wallet.address.toLowerCase() === linkedExternalSolanaAddress.toLowerCase(),
      );
      if (matchingWallet) return matchingWallet;
    }

    // If no linked external wallet or no match, return null to trigger connect flow
    // This prevents accidentally using the embedded wallet
    return null;
  }, [standardWallets, linkedExternalSolanaAddress]);

  // Detect wallet mismatch: user has a linked wallet AND an active injected wallet,
  // but the active wallet doesn't match the linked one (user switched accounts in Phantom)
  const isWalletMismatch = useMemo(() => {
    if (!linkedExternalSolanaAddress || !activeInjectedWallet) return false;
    return activeInjectedWallet.address.toLowerCase() !== linkedExternalSolanaAddress.toLowerCase();
  }, [linkedExternalSolanaAddress, activeInjectedWallet]);

  // Use the already-computed linked external address
  // This excludes embedded wallets (Privy-created wallets that can't be unlinked)
  const linkedSolanaAddress = linkedExternalSolanaAddress;

  // Check if the current Solana wallet is already linked to the user's account
  const isSolanaLinked = useMemo(() => {
    if (!solanaWallet || !linkedSolanaAddress) return false;
    return linkedSolanaAddress.toLowerCase() === solanaWallet.address.toLowerCase();
  }, [solanaWallet, linkedSolanaAddress]);

  // Check if we have a linked Solana address (even if wallet not currently connected)
  const hasLinkedSolanaWallet = !!linkedSolanaAddress;

  /**
   * Connects and links a Solana wallet in one step.
   * Uses linkWallet for linking (required for backend verification),
   * with specific wallet list (Phantom, Solflare, WalletConnect).
   *
   * If the wallet is already linked to another EVM account, sets linkError
   * with a SolanaWalletAlreadyLinkedError to show helpful guidance.
   */
  const connectAndLinkSolanaWallet = useCallback(async () => {
    setLinkError(null);
    setSolanaSignerStatus(WALLET_SIGNER_STATUS.UNKNOWN);
    try {
      // linkWallet handles both connect + link in one flow
      // Only show Phantom, Solflare, and WalletConnect for Solana
      await linkWallet({
        walletChainType: "solana-only",
        walletList: ["phantom", "solflare", "wallet_connect"],
      });
    } catch (error) {
      // Check if this is an "already linked" error from Privy
      const errorMessage = error instanceof Error ? error.message.toLowerCase() : "";
      if (
        errorMessage.includes("already linked") ||
        errorMessage.includes("linked to another") ||
        errorMessage.includes("account_already_exists")
      ) {
        setLinkError(new SolanaWalletAlreadyLinkedError());
      } else {
        setLinkError(error instanceof Error ? error : new Error("Failed to link Solana wallet"));
      }
    }
  }, [linkWallet, setSolanaSignerStatus]);

  /**
   * Clears any link error state.
   * Call this when user dismisses the error or tries again.
   */
  const clearLinkError = useCallback(() => {
    setLinkError(null);
  }, []);

  /**
   * Unlinks the Solana wallet from this EVM account.
   * After unlinking, the Solana wallet can be linked to a different EVM account.
   *
   * NOTE: This permanently removes the link. The user will need to re-link
   * if they want to use this Solana wallet with this EVM account again.
   */
  const unlinkSolanaWallet = useCallback(async () => {
    if (!linkedSolanaAddress) return;

    setIsUnlinking(true);
    try {
      await unlinkWallet(linkedSolanaAddress);
      setSolanaSignerStatus(WALLET_SIGNER_STATUS.UNKNOWN);
    } finally {
      setIsUnlinking(false);
    }
  }, [linkedSolanaAddress, setSolanaSignerStatus, unlinkWallet]);

  /**
   * Disconnects the currently connected Solana wallet.
   * This does NOT affect the EVM wallet or user session.
   * NOTE: This only disconnects the session, it does NOT unlink the wallet.
   */
  const disconnectSolanaWallet = useCallback(async () => {
    if (solanaWallet) {
      await solanaWallet.disconnect();
    }
    setSolanaSignerStatus(WALLET_SIGNER_STATUS.UNKNOWN);
  }, [setSolanaSignerStatus, solanaWallet]);

  useEffect(() => {
    if (!linkedSolanaAddress || isWalletMismatch || !solanaWallet) {
      setSolanaSignerStatus(WALLET_SIGNER_STATUS.UNKNOWN);
    }
  }, [isWalletMismatch, linkedSolanaAddress, setSolanaSignerStatus, solanaWallet]);

  return {
    /** The connected Solana wallet object (for signing transactions). Use solanaWallet?.address for the address. */
    solanaWallet,
    /** Whether a Solana wallet is currently connected and matches the linked wallet */
    isSolanaConnected: !!solanaWallet,
    /** Whether the connected Solana wallet is linked to the user's Privy account */
    isSolanaLinked,
    /** Whether the user has ANY linked Solana wallet (even if not currently connected) */
    hasLinkedSolanaWallet,
    /** The address of the linked Solana wallet (if any) */
    linkedSolanaAddress,
    /** Connect and link a Solana wallet in one step (opens Privy modal) */
    connectAndLinkSolanaWallet,
    /** Disconnect the current Solana wallet (session only, does NOT unlink) */
    disconnectSolanaWallet,
    /** Unlink the Solana wallet from this EVM account (allows linking to different account) */
    unlinkSolanaWallet,
    /** Whether an unlink operation is in progress */
    isUnlinking,
    /** Error from linking attempt (e.g., wallet already linked to another account) */
    linkError,
    /** Clear the link error state */
    clearLinkError,
    /** Check if the error is specifically about wallet being linked to another account */
    isAlreadyLinkedError: linkError instanceof SolanaWalletAlreadyLinkedError,
    /** Whether the active Phantom wallet doesn't match the linked wallet (user switched accounts) */
    isWalletMismatch,
    /** The currently active injected wallet address (may differ from linked) */
    activeInjectedWalletAddress: activeInjectedWallet?.address ?? null,
    /** Whether the linked Solana wallet needs to be unlocked before signing */
    solanaSignerStatus,
    isSolanaLocked: hasLinkedSolanaWallet && solanaSignerStatus === WALLET_SIGNER_STATUS.LOCKED,
    // Note: NO identity token or auth-related fields here
    // Solana wallet is ONLY for transaction signing
  };
};
